import { useCallback, useEffect, useRef, useState } from "react";
import {
  SELECTION_CHANGE_COMMAND,
  $getSelection,
  $isRangeSelection,
  type LexicalEditor,
  type BaseSelection,
  type RangeSelection,
} from "lexical";
import { mergeRegister } from "@lexical/utils";
import { $isLinkNode, TOGGLE_LINK_COMMAND } from "@lexical/link";
import { $isAtNodeEnd } from "@lexical/selection";
import "./index.css";
import Icon from "../Icon";

interface FloatingLinkEditorProps {
  editor: LexicalEditor;
}

function FloatingLinkEditor({ editor }: FloatingLinkEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const mouseDownRef = useRef(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [isEditMode, setEditMode] = useState(false);
  const [isLink, setIsLink] = useState(false);
  const [lastSelection, setLastSelection] = useState<BaseSelection | null>(null);

  const updateLinkEditor = useCallback(() => {
    const nativeSelection = window.getSelection();
    const selection = $getSelection();
    let isLinkActive = false;
    let newLinkUrl = "";

    if ($isRangeSelection(selection)) {
      const node = getSelectedNode(selection);
      const parent = node.getParent();
      if ($isLinkNode(parent)) {
        newLinkUrl = parent.getURL();
        isLinkActive = true;
      } else if ($isLinkNode(node)) {
        newLinkUrl = node.getURL();
        isLinkActive = true;
      }
    }

    setLinkUrl(newLinkUrl);
    setIsLink(isLinkActive);

    const editorElem = editorRef.current;

    const activeElement = document.activeElement;

    if (!editorElem) return;

    const rootElement = editor.getRootElement();

    if (
      selection &&
      nativeSelection &&
      rootElement &&
      rootElement.contains(nativeSelection.anchorNode) &&
      (isEditMode || !nativeSelection.isCollapsed || isLinkActive || newLinkUrl !== "")
    ) {
      const domRange = nativeSelection.getRangeAt(0);
      let rect;
      if (nativeSelection.anchorNode === rootElement) {
        let inner: HTMLElement | Element = rootElement;
        while (inner.firstElementChild != null) inner = inner.firstElementChild;
        rect = inner.getBoundingClientRect();
      } else {
        rect = domRange.getBoundingClientRect();
      }

      if (!mouseDownRef.current) {
        positionEditorElement(editorElem, rect);
      }
      setLastSelection(selection);
    } else if (!activeElement || activeElement.className !== "link-input") {
      positionEditorElement(editorElem, null);
      setLastSelection(null);
      setEditMode(false);
      setLinkUrl("");
    }

    return true;
  }, [editor]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }: { editorState: any }) => {
        editorState.read(() => {
          updateLinkEditor();
        });
      }),

      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateLinkEditor();
          return true;
        },
        1
      )
    );
  }, [editor, updateLinkEditor]);

  useEffect(() => {
    editor.getEditorState().read(() => {
      updateLinkEditor();
    });
  }, [editor, updateLinkEditor]);

  useEffect(() => {
    if (isEditMode && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditMode]);

  useEffect(() => {
    if (linkUrl === "https://") {
      setEditMode(true);
    }
  }, [linkUrl]);

  return (
    <div className="floating-link-editor" ref={editorRef}>
      {isEditMode ? (
        <>
          <input
            ref={inputRef}
            className="link-input"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                if (lastSelection && linkUrl !== "") {
                  editor.dispatchCommand(TOGGLE_LINK_COMMAND, linkUrl);
                }
                setEditMode(false);
              } else if (e.key === "Escape") {
                e.preventDefault();
                setEditMode(false);
              }
            }}
            placeholder="Enter link..."
          />
          <button
            className="link-button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              if (isEditMode) editor.dispatchCommand(TOGGLE_LINK_COMMAND, linkUrl);
              setEditMode(false);
            }}>
            <Icon name="Check" />
          </button>
        </>
      ) : (
        <div className="link-view-mode">
          <span className="link-view-label">Go to link:</span>
          <a href={linkUrl} target="_blank" rel="noopener noreferrer" className="link-view-url">
            {linkUrl}
          </a>
          <span className="link-view-divider">|</span>
          <button
            className="link-text-button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setEditMode(true)}
          >
            Change
          </button>
          <span className="link-view-divider">|</span>
          <button
            className="link-text-button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
              setEditMode(false);
            }}
          >
            Remove
          </button>
        </div>
      )}
    </div>
  );
}

function getSelectedNode(selection: RangeSelection) {
  const anchorNode = selection.anchor.getNode();
  const focusNode = selection.focus.getNode();
  if (anchorNode === focusNode) return anchorNode;

  const isBackward = selection.isBackward();
  return isBackward
    ? $isAtNodeEnd(selection.focus)
      ? anchorNode
      : focusNode
    : $isAtNodeEnd(selection.anchor)
      ? focusNode
      : anchorNode;
}

// 🧭 Smart position logic to prevent going off-screen
function positionEditorElement(editor: HTMLElement, rect: DOMRect | null) {
  if (!rect) {
    editor.style.opacity = "0";
    editor.style.top = "-1000px";
    editor.style.left = "-1000px";
    return;
  }

  const editorWidth = editor.offsetWidth;
  const editorHeight = editor.offsetHeight;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  // Default position (below selection)
  let top = rect.top + rect.height + window.pageYOffset + 10;
  let left = rect.left + window.pageXOffset - editorWidth / 2 + rect.width / 2;

  // 🧩 Clamp horizontally
  if (left < 8) left = 8;
  if (left + editorWidth > viewportWidth - 8)
    left = viewportWidth - editorWidth - 8;

  // 🧩 Clamp vertically
  if (top + editorHeight > viewportHeight - 8) {
    top = rect.top + window.pageYOffset - editorHeight - 10; // place above
  }
  if (top < 8) top = 8;

  editor.style.opacity = "1";
  editor.style.top = `${top}px`;
  editor.style.left = `${left}px`;
}

export default FloatingLinkEditor;
